import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogResult } from '@app/Common/popup/dialog-result';
import { Permissions } from '@app/Models/Permissions';
import { PermissionService } from '@app/Services/permission.service';
import { IAuthProviderTranslation } from '../Models/IAuthProviderTranslation';
import { PassportAdminService } from '../Services/passport.admin.service';
import { AddSamlPopup } from './addSamlPopup.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { JsonPipe } from '@angular/common';
import { MatInput, MatFormField, MatLabel, MatHint } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';

const urlRegex = /[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/;

interface INewProvider {
    loginType: number;
    name: string;
    isEnabled: boolean;
    adminOnly: boolean;
}

interface IProvider extends INewProvider {
    providerId: string;
}

@Component({
    selector: 'my-package',
    templateUrl: './providers.component.html',
    styleUrls: ['./providers.component.css', '../passport.component.css'],
    imports: [MatButton, MatIcon, MatInput, FormsModule, MatFormField, MatLabel, MatCheckbox, MatSelect, MatOption, MatHint, JsonPipe]
})
export class ProvidersComponent implements OnInit {
    public authProviders: IProvider[];
    public editingProvider: false | INewProvider;
    public editProviderForm: any = {};
    public isSaving: boolean;
    public managingLocalisation: false | IProvider;
    public translations: IAuthProviderTranslation[];
    public newLanguageCode?: string;
    public newTranslation?: string;
    public errors: any[] = [];
    public isReadOnly: boolean;

    constructor(
        private service: PassportAdminService,
        public dialog: MatDialog,
        private permissionService: PermissionService) {
    }

    public ngOnInit(): void {
        this.service.getTenantDetailsAsync()
            .then(tenant => {
                this.getAuthProviders();
            });
        this.isReadOnly = this.permissionService.hasPermission(Permissions.passportReadOnly);
    }

    private getAuthProviders(): void {
        this.service
            .getAuthProviders()
            .subscribe(authProviders => this.authProviders = authProviders);
    }

    public getLoginType(type: number): string {
        switch (type) {
            case 1:
                return 'Username and Password';
            case 2:
                return 'SAML';
            case 6:
                return 'Guest Account';
            default:
                return '';
        }
    }

    public editProvider(prov: IProvider): void {
        this.editingProvider = { ...prov };
        this.managingLocalisation = false;
    }

    public saveProvider(prov: IProvider) {
        this.isSaving = true;
        this.service.saveAuthProvider(prov)
            .subscribe({
                complete: () => {
                    if (prov.loginType === 6 && prov.isEnabled) {
                        this.service.createGuestAccount(prov).subscribe({
                            complete: () => { this.reloadAuthProvidersAfterEdit(); },
                            next: undefined,
                            error: undefined
                        });
                    }
                    else {
                        this.reloadAuthProvidersAfterEdit();
                    }
                },
                next: undefined,
                error: undefined
            });

        return false;
    }

    private reloadAuthProvidersAfterEdit(): void {
        this.editingProvider = false;
        this.isSaving = false;
        this.getAuthProviders();
    }

    public deleteProvider(prov: IProvider): void {
        this.service.deleteAuthProvider(prov)
            .subscribe(response => this.getAuthProviders());
    }

    public manageLocalisation(prov: IProvider): void {
        this.editingProvider = false;
        this.managingLocalisation = prov;
        const instance = this;

        this.service
            .getAuthProviderTranslations(prov.providerId)
            .subscribe(translations => this.translations = translations.sort(instance.translationSort));
    }

    public updateLocalisation(prov: IProvider, languageCode: string, translation: string): void {
        if (!languageCode || !translation) { return; }

        const newTranslation = {
            translation,
            languageCode
        };

        function predicate(translation: IAuthProviderTranslation) {
            return translation.languageCode.localeCompare(languageCode, undefined, { sensitivity: 'accent' }) !== 0;
        }

        this.translations = this.translations
            .filter(predicate)
            .concat(newTranslation)
            .sort(this.translationSort);

        this.newLanguageCode = undefined;
        this.newTranslation = undefined;

        const instance = this;
        this.service
            .updateAuthProviderTranslation(prov.providerId, newTranslation)
            .subscribe(() => instance.manageLocalisation(prov));
    }

    private translationSort(translationA: IAuthProviderTranslation, translationB: IAuthProviderTranslation) {
        if (translationA.languageCode < translationB.languageCode) { return -1; }
        else if (translationA.languageCode > translationB.languageCode) { return 1; }
        else if (translationA.translation < translationB.translation) { return -1; }
        else if (translationA.translation > translationB.translation) { return 1; }
        else { return 0; }
    }

    public addSamlProvider(): void {
        const instance = this;
        this.errors = [];

        const dialogRef = this.dialog.open(AddSamlPopup);
        dialogRef.componentInstance.dialogTitle = 'Add new auth provider';
        dialogRef.componentInstance.dialogDescription = 'Metadata URL can be left blank to manually configure';
        dialogRef.componentInstance.dialogConfirmButtonText = 'Create';
        dialogRef.componentInstance.dialogCancelButtonText = 'Cancel';
        dialogRef.componentInstance.displayTextBox = true;

        function onSubmit(result: DialogResult) {
            if (!result.Result) {
                return;
            }

            const metaUrl = dialogRef.componentInstance.metadataUrl;
            const providerName = dialogRef.componentInstance.name;

            if (!metaUrl) {
                instance.addBlankProvider(providerName);
                return;
            }

            if (urlRegex.test(metaUrl)) {
                instance.service
                    .createAuthProvider(providerName, metaUrl)
                    .then(() => instance.getAuthProviders())
                    .catch(e => instance.errors.push(e));
            }
            else {
                instance.errors.push({ Message: 'Meta URL invalid, skipping adding', Regex: urlRegex, MetaURL: metaUrl });
            }
        }

        dialogRef.afterClosed().subscribe(onSubmit);
    }

    private addBlankProvider(name: string): void {
        this.editingProvider = {
            loginType: 2, // SAML
            name,
            isEnabled: true,
            adminOnly: true
        };
    }
}
